// Cycle counts (docs/design/cycle-counts.md, ADR-017).
//
//   list      stock_counts?select=…,warehouse,company&order=created_at.desc,id.desc&limit=51
//             keyset on (created_at, id): stock_counts_list_idx / stock_counts_status_idx.
//   header    rpc stock_count_document(id) — header + counted locations, one round-trip.
//   lines     rpc stock_count_line_page(id, after, limit, filter) — keyset on line_no
//             (stock_count_lines_line_key), 100 lines ≈ 30 KB per page.
//   writes    rpc save_ / start_ / enter_ / post_ / cancel_stock_count; the tables are
//             read-only for API users. System quantities come back only for
//             inventory.count.approve (blind counting).

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoApi } from "./demo";
import type { ListFilter, ListSort } from "@patronix/domain/list";
import { applyRestFilters, applyRestSort, restCursorOf, type RestColumn, type RestCursor } from "@/lib/restList";

export type CountStatus = "draft" | "in_progress" | "posted" | "cancelled";
export type CountStatusFilter = "all" | CountStatus;
export type LineFilter = "all" | "uncounted" | "differences";

export interface CountScope {
  location_ids?: string[];
  zone?: string;
  code_from?: string;
  code_to?: string;
  item_ids?: string[];
}

export interface CountRow {
  id: string;
  number: string | null;
  status: CountStatus;
  note: string | null;
  line_count: number;
  counted_count: number;
  location_count: number;
  created_at: string;
  started_at: string | null;
  posted_at: string | null;
  warehouse: { code: string; name: string } | null;
  company: { code: string } | null;
}

export interface CountDocument {
  id: string;
  number: string | null;
  status: CountStatus;
  company_id: string;
  company_code: string;
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  scope: CountScope;
  note: string | null;
  line_count: number;
  counted_count: number;
  location_count: number;
  show_system: boolean;
  created_at: string;
  started_at: string | null;
  posted_at: string | null;
  cancelled_at: string | null;
  updated_at: string;
  locations: { id: string; code: string }[];
}

export interface CountLine {
  id: string;
  line_no: number;
  item_code: string;
  item_description: string;
  location_code: string;
  lot_code: string | null;
  is_new_lot: boolean;
  /** Project stock line (PR 3a); null = free stock. */
  project_code?: string | null;
  uom_code: string;
  uom_decimals: number;
  system_qty: number | string | null;
  counted_qty: number | string | null;
  difference: number | string | null;
  is_added: boolean;
  /** Serial-controlled item: counted by scanning (serials PR 2b). */
  serial_controlled?: boolean;
  /** Scanned units (null = not counted); the list itself loads with stock_count_line_serials. */
  counted_serial_count?: number | null;
  /** Approver view: units in stock there but not scanned / scanned but not in stock there. */
  serial_missing?: number | null;
  serial_found?: number | null;
}

/** A counted quantity: by line, or by stock key (found stock becomes an added line). */
export type CountEntry =
  | { line_id: string; qty: string; uom_id?: string | undefined }
  /** A serial line: the scanned units are the count (an empty list = none there). */
  | { line_id: string; serials: string[] }
  | {
      item_id: string;
      location_id: string;
      lot_id?: string | undefined;
      lot_code?: string | undefined;
      qty: string;
      uom_id?: string | undefined;
    };

export interface EnterResult {
  id: string;
  line_count: number;
  counted_count: number;
  added: number;
  lines: { id: string; line_no: number; counted_qty: number | string; is_added: boolean }[];
}

export interface CountActionResult {
  id: string;
  number?: string | null;
  status: CountStatus;
  line_count?: number;
  location_count?: number;
  differences?: number;
}

export const COUNT_PAGE = 50;
export const LINE_PAGE = 100;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

const listColumns =
  "id,number,status,note,line_count,counted_count,location_count,created_at,started_at,posted_at," +
  "warehouse:warehouses!stock_counts_warehouse_fkey(code,name),company:companies!stock_counts_company_fkey(code)";

// Column filters and sort (stockCountListColumns) on the table's own columns;
// default newest first. "progress" filters and sorts by counted lines.
const restColumns: RestColumn[] = [
  { key: "number", column: "number", type: "text", nullable: true },
  { key: "progress", column: "counted_count", type: "number" },
  { key: "date", column: "created_at", type: "date", timestamp: true },
  { key: "note", column: "note", type: "text", nullable: true },
  { key: "status", column: "status", type: "enum" },
];
const defaultSort: ListSort = { col: "date", dir: "desc" };

export interface CountListView {
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export async function listCounts(status: CountStatusFilter, cursor: string | null, signal: AbortSignal, view: CountListView = {}): Promise<Page<CountRow>> {
  if (isDemoMode) return demoApi.list(status, cursor);
  let query = db().from("stock_counts").select(listColumns);
  if (status !== "all") query = query.eq("status", status);
  query = applyRestFilters(query, restColumns, view.filters ?? []);
  query = applyRestSort(query, restColumns, view.sort ?? null, defaultSort, cursor ? (JSON.parse(cursor) as RestCursor) : null);
  const { data, error } = await query.limit(COUNT_PAGE + 1).abortSignal(signal);
  if (error) throw error;
  const rows = data as unknown as CountRow[];
  const hasMore = rows.length > COUNT_PAGE;
  const page = hasMore ? rows.slice(0, COUNT_PAGE) : rows;
  const last = page[page.length - 1];
  return {
    rows: page,
    nextCursor: hasMore && last ? restCursorOf(last as unknown as Record<string, unknown>, restColumns, view.sort ?? null, defaultSort) : null,
  };
}

async function getCount(id: string, signal: AbortSignal): Promise<CountDocument | null> {
  if (isDemoMode) return demoApi.get(id);
  const { data, error } = await db().rpc("stock_count_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return data as CountDocument | null;
}

async function linePage(id: string, filter: LineFilter, after: number, signal: AbortSignal): Promise<Page<CountLine>> {
  const rows = isDemoMode
    ? await demoApi.lines(id, filter, after, LINE_PAGE + 1)
    : await (async () => {
        const { data, error } = await db()
          .rpc("stock_count_line_page", { p_id: id, p_after: after, p_limit: LINE_PAGE + 1, p_filter: filter })
          .abortSignal(signal);
        if (error) throw error;
        return data as CountLine[];
      })();
  const hasMore = rows.length > LINE_PAGE;
  const page = hasMore ? rows.slice(0, LINE_PAGE) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? String(last.line_no) : null };
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw error;
  return data as T;
}

export function saveCount(
  id: string | null,
  companyId: string,
  warehouseId: string,
  scope: CountScope,
  note: string,
): Promise<string> {
  if (isDemoMode) return demoApi.save(id, warehouseId, scope, note);
  return rpc<string>("save_stock_count", {
    p_id: id,
    p_company_id: companyId,
    p_warehouse_id: warehouseId,
    p_scope: scope,
    p_note: note,
  });
}

export function startCount(id: string): Promise<CountActionResult> {
  if (isDemoMode) return demoApi.start(id);
  return rpc<CountActionResult>("start_stock_count", { p_id: id });
}

export function enterCount(id: string, entries: CountEntry[]): Promise<EnterResult> {
  if (isDemoMode) return demoApi.enter(id, entries);
  return rpc<EnterResult>("enter_stock_count", { p_id: id, p_lines: entries });
}

/** The scanned serials of one count line (scan dialog). */
export async function lineSerials(lineId: string): Promise<string[]> {
  if (isDemoMode) return [];
  const data = await rpc<string[] | null>("stock_count_line_serials", { p_line_id: lineId });
  return data ?? [];
}

export function postCount(id: string): Promise<CountActionResult> {
  if (isDemoMode) return demoApi.post(id);
  return rpc<CountActionResult>("post_stock_count", { p_id: id });
}

export function cancelCount(id: string): Promise<CountActionResult> {
  if (isDemoMode) return demoApi.cancel(id);
  return rpc<CountActionResult>("cancel_stock_count", { p_id: id });
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export const countKeys = {
  all: ["warehousing", "counts"] as const,
  list: (status: CountStatusFilter, view: CountListView = {}) => [...countKeys.all, "list", status, view] as const,
  detail: (id: string) => [...countKeys.all, "detail", id] as const,
  lines: (id: string, filter: LineFilter) => [...countKeys.all, "lines", id, filter] as const,
};

export function countListQuery(status: CountStatusFilter, view: CountListView = {}) {
  return infiniteQueryOptions({
    queryKey: countKeys.list(status, view),
    queryFn: ({ pageParam, signal }) => listCounts(status, pageParam, signal, view),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<CountRow>) => last.nextCursor,
    staleTime: 30_000,
  });
}

export function countDetailQuery(id: string) {
  return queryOptions({
    queryKey: countKeys.detail(id),
    queryFn: ({ signal }) => getCount(id, signal),
    staleTime: 15_000,
  });
}

export function countLinesQuery(id: string, filter: LineFilter) {
  return infiniteQueryOptions({
    queryKey: countKeys.lines(id, filter),
    queryFn: ({ pageParam, signal }) => linePage(id, filter, Number(pageParam ?? 0), signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<CountLine>) => last.nextCursor,
    staleTime: 15_000,
  });
}
